import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import invariant from 'tiny-invariant';
import Value from '../models/Value';
import History from '../models/History';
import Selection from '../models/Selection';
import Data from '../models/Data';
import CorePlugin, { CommandsPlugin, QueriesPlugin } from '../plugins/CorePlugin';

var Database =
/*#__PURE__*/
function () {
  function Database() {
    var attrs = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

    _classCallCheck(this, Database);

    var _attrs$readOnly = attrs.readOnly,
        readOnly = _attrs$readOnly === void 0 ? false : _attrs$readOnly,
        _attrs$value = attrs.value,
        value = _attrs$value === void 0 ? null : _attrs$value,
        _attrs$selection = attrs.selection,
        selection = _attrs$selection === void 0 ? null : _attrs$selection,
        _attrs$onChange = attrs.onChange,
        onChange = _attrs$onChange === void 0 ? function () {} : _attrs$onChange,
        _attrs$plugins = attrs.plugins,
        plugins = _attrs$plugins === void 0 ? [] : _attrs$plugins,
        _attrs$currentUserId = attrs.currentUserId,
        currentUserId = _attrs$currentUserId === void 0 ? 'anonymous' : _attrs$currentUserId;
    this.currentUserId = currentUserId;
    this.readOnly = readOnly;
    this.value = value;
    this.middleware = {};
    this.onChange = onChange;
    this.plugins = plugins;
    this.operations = [];
    this.flushing = false;
    this.value = value || Value.create({}, this.currentUserId);
    this.history = History.create();
    this.selection = selection || Selection.create();
    this.data = Data.create();
    this.tmp = {
      save: true
    };
    var core = CorePlugin({
      plugins: plugins
    });
    registerPlugin(this, core);
    this.run('onConstruct');
    return this;
  }

  _createClass(Database, [{
    key: "applyAction",
    value: function applyAction(action) {
      var _Value$applyAction = Value.applyAction(this.value, action),
          value = _Value$applyAction.value,
          changes = _Value$applyAction.changes,
          inverseChanges = _Value$applyAction.inverseChanges;

      this.applyChanges({
        action: action,
        value: value,
        changes: changes,
        inverseChanges: inverseChanges
      });
    }
  }, {
    key: "applyChanges",
    value: function applyChanges(_ref) {
      var _this = this;

      var action = _ref.action,
          value = _ref.value,
          changes = _ref.changes,
          inverseChanges = _ref.inverseChanges;
      this.value = value;
      this.operations = this.operations.concat({
        action: action,
        changes: changes,
        inverseChanges: inverseChanges
      });

      if (!this.flushing) {
        this.flushing = true;
        var willSave = this.tmp.save;
        Promise.resolve().then(function () {
          return _this.flush(willSave);
        });
      }

      return this;
    }
  }, {
    key: "flush",
    value: function flush() {
      var save = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;
      this.run('onChange');
      var value = this.value,
          operations = this.operations,
          selection = this.selection,
          data = this.data;
      var change = {
        value: value,
        operations: operations,
        selection: selection,
        data: data
      };

      if (save) {
        this.save(operations);
      }

      this.operations = [];
      this.flushing = false;
      this.onChange(change);
      return this;
    } // Run through the middleware stack by `key` with `args`.

  }, {
    key: "run",
    value: function run(key) {
      for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
        args[_key - 1] = arguments[_key];
      }

      var middleware = this.middleware;
      var controller = this;
      var fns = middleware[key] || [];
      var i = 0;

      function next() {
        var fn = fns[i++];
        if (!fn) return;

        for (var _len2 = arguments.length, overrides = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
          overrides[_key2] = arguments[_key2];
        }

        if (overrides.length) {
          args = overrides;
        }

        var ret = fn.apply(void 0, _toConsumableArray(args).concat([controller, next]));
        return ret;
      }

      return next();
    }
  }, {
    key: "registerCommand",
    value: function registerCommand(type) {
      var _this2 = this;

      var controller = this;

      if (type in controller && controller[type].__command) {
        return controller;
      }

      invariant(!(type in controller), "You cannot register a `".concat(type, "` command because it would overwrite an existing property of the `Database`."));

      var method = function method() {
        for (var _len3 = arguments.length, args = new Array(_len3), _key3 = 0; _key3 < _len3; _key3++) {
          args[_key3] = arguments[_key3];
        }

        return _this2.command.apply(_this2, [type].concat(args));
      };

      controller[type] = method;
      method.__command = true;
      return controller;
    }
  }, {
    key: "command",
    value: function command(type) {
      var controller = this;

      for (var _len4 = arguments.length, args = new Array(_len4 > 1 ? _len4 - 1 : 0), _key4 = 1; _key4 < _len4; _key4++) {
        args[_key4 - 1] = arguments[_key4];
      }

      if (typeof type === 'function') {
        type.apply(void 0, [controller].concat(args));
        return controller;
      }

      var obj = {
        type: type,
        args: args
      };
      this.run('onCommand', obj);
      return controller;
    }
  }, {
    key: "registerQuery",
    value: function registerQuery(type) {
      var _this3 = this;

      var controller = this;

      if (type in controller && controller[type].__query) {
        return controller;
      }

      invariant(!(type in controller), "You cannot register a `".concat(type, "` query because it would overwrite an existing property of the `Database`."));

      var method = function method() {
        for (var _len5 = arguments.length, args = new Array(_len5), _key5 = 0; _key5 < _len5; _key5++) {
          args[_key5] = arguments[_key5];
        }

        return _this3.query.apply(_this3, [type].concat(args));
      };

      controller[type] = method;
      method.__query = true;
      return controller;
    }
  }, {
    key: "query",
    value: function query(type) {
      var controller = this;

      for (var _len6 = arguments.length, args = new Array(_len6 > 1 ? _len6 - 1 : 0), _key6 = 1; _key6 < _len6; _key6++) {
        args[_key6 - 1] = arguments[_key6];
      }

      if (typeof type === 'function') {
        return type.apply(void 0, [controller].concat(args));
      }

      var obj = {
        type: type,
        args: args
      };
      return this.run('onQuery', obj);
    }
  }, {
    key: "withoutSaving",
    value: function withoutSaving(fn) {
      this.tmp.save = false;
      fn(this);
      this.tmp.save = true;
      return this;
    }
  }, {
    key: "setValue",
    value: function setValue(value) {
      this.value = value;
    }
  }, {
    key: "setHistory",
    value: function setHistory(history) {
      this.history = history;
    }
  }, {
    key: "setSelection",
    value: function setSelection(selection) {
      var _this4 = this;

      this.selection = selection; // We trigger an action without side effects to enqueue an update

      return this.withoutSaving(function () {
        return _this4.applyAction({
          type: 'setSelection',
          payload: {
            selection: selection
          }
        });
      });
    }
  }, {
    key: "setData",
    value: function setData(data) {
      var _this5 = this;

      this.data = data; // We trigger an action without side effects to enqueue an update

      return this.withoutSaving(function () {
        return _this5.applyAction({
          type: 'setData',
          payload: {
            data: data
          }
        });
      });
    }
  }, {
    key: "setOnChange",
    value: function setOnChange() {
      var fn = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : function () {};
      this.onChange = fn;
    }
  }, {
    key: "setCurrentUserId",
    value: function setCurrentUserId(currentUserId) {
      this.currentUserId = currentUserId;
    }
  }, {
    key: "setReadOnly",
    value: function setReadOnly(readOnly) {
      this.readOnly = readOnly;
    }
  }, {
    key: "setElement",
    value: function setElement(element) {
      this.element = element;
    }
  }, {
    key: "setAttributes",
    value: function setAttributes() {
      var attrs = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      return this.applyAction({
        type: 'setAttributes',
        payload: {
          attrs: attrs,
          currentUserId: this.currentUserId
        }
      });
    }
  }], [{
    key: "create",
    value: function create(attrs) {
      return new Database(attrs);
    }
  }]);

  return Database;
}();

function registerPlugin(database, plugin) {
  if (Array.isArray(plugin)) {
    plugin.forEach(function (p) {
      return registerPlugin(database, p);
    });
    return;
  }

  if (plugin == null) {
    return;
  }

  var commands = plugin.commands,
      queries = plugin.queries,
      rest = _objectWithoutProperties(plugin, ["commands", "queries"]);

  if (commands) {
    var commandsPlugin = CommandsPlugin(commands);
    registerPlugin(database, commandsPlugin);
  }

  if (queries) {
    var queriesPlugin = QueriesPlugin(queries);
    registerPlugin(database, queriesPlugin);
  }

  for (var key in rest) {
    var fn = rest[key];
    var middleware = database.middleware[key] = database.middleware[key] || [];
    middleware.push(fn);
  }
}

export default Database;